export type Letters = readonly [string, string, string];

export type Segments = { a: number; b: number; c: number; d: number };
export type Positions = { l1: number; l2: number; l3: number };

export type ScoringResult = {
  positions: Positions;
  segments: Segments;
  rawScore: number;
};

export function normalizeForScoring(word: string): string {
  return word.toLowerCase().replace(/-/g, '');
}

export function findOptimalPositions(word: string, letters: Letters): Positions | null {
  const n = word.length;
  const [a, b, c] = letters;

  let l1 = -1;
  for (let i = 0; i < n; i++) if (word[i] === a) { l1 = i; break; }
  if (l1 === -1) return null;

  let l3 = -1;
  for (let i = n - 1; i > l1; i--) if (word[i] === c) { l3 = i; break; }
  if (l3 === -1) return null;

  let l2 = -1;
  for (let i = l1 + 1; i < l3; i++) if (word[i] === b) { l2 = i; break; }

  if (l2 === -1) {
    for (let i = l1 + 1; i < n; i++) {
      if (word[i] === a) {
        const next = findOptimalPositions(word.slice(i), letters);
        if (!next) continue;
        return { l1: next.l1 + i, l2: next.l2 + i, l3: next.l3 + i };
      }
    }
    return null;
  }
  return { l1, l2, l3 };
}

export function computeSegments(word: string, p: Positions): Segments {
  return {
    a: p.l1,
    b: Math.max(0, p.l2 - p.l1 - 1),
    c: Math.max(0, p.l3 - p.l2 - 1),
    d: word.length - p.l3 - 1,
  };
}

export function scoreFromSegments(s: Segments): number {
  return 2 * s.a + 3 * s.b + 3 * s.c + 2 * s.d;
}

export function score(word: string, letters: Letters): ScoringResult | null {
  const w = normalizeForScoring(word);
  const positions = findOptimalPositions(w, letters);
  if (!positions) return null;
  const segments = computeSegments(w, positions);
  return { positions, segments, rawScore: scoreFromSegments(segments) };
}
